(() => {
  'use strict';

  const namespace = 'DawsonJonesCollectionColorSelector';

  if (window[namespace]?.initialized) {
    window[namespace].refresh?.();
    return;
  }

  const api = {
    initialized: true,
    refresh: null
  };
  window[namespace] = api;

  const CARD_SELECTOR = 'product-card[data-collection-color-selector-enabled="true"]';
  const CONTROL_SELECTOR =
    '[data-collection-color-option-group="true"] input.js-card-swatch-input[type="radio"][data-option-value]';
  const DEFAULT_COLOR_MARKER_SELECTOR =
    '[data-collection-default-color-enabled][data-collection-default-color]';
  const PAGE_SETTING_MARKER_SELECTOR =
    '[data-collection-color-buttons-enabled], [data-collection-default-color-enabled], [data-collection-default-color]';
  const DEFAULT_COLOR_APPLIED_ATTRIBUTE = 'data-collection-default-color-applied';
  const TOOLBAR_SELECTOR = '[data-collection-color-selector-toolbar]';
  const TOOLBAR_ID = 'collection-color-selector-toolbar';
  const STYLE_ID = 'collection-color-selector-styles';

  let toolbar = null;
  let refreshScheduled = false;
  let applyingColor = false;
  let userSelectedColorKey = null;

  const normalizeColor = (value) => String(value || '')
    .toLowerCase()
    .trim()
    .replace(/[_-]+/g, ' ')
    .replace(/\bgrey\b/g, 'gray')
    .replace(/\baluminium\b/g, 'aluminum')
    .replace(/\bbarn\s+wood\b/g, 'barnwood')
    .replace(/\s+/g, ' ');

  const STANDARD_COLORS = [
    'Black',
    'White',
    'Maple',
    'Cherry',
    'Walnut',
    'Rustic Gray',
    'Barnwood',
    'Aluminum'
  ].map((label, index) => ({
    key: normalizeColor(label),
    label,
    index
  }));

  const standardColorByKey = new Map(
    STANDARD_COLORS.map((color) => [color.key, color])
  );

  const getPageSettingMarker = () => {
    const productSectionMarker = document.querySelector(
      '[data-collection-color-buttons-owner="product-section"]'
    );
    if (productSectionMarker) return productSectionMarker;

    const markers = Array.from(document.querySelectorAll(
      '[data-collection-color-buttons-enabled]'
    ));
    return markers.length ? markers[markers.length - 1] : null;
  };

  const pageIsDisabled = () => (
    getPageSettingMarker()?.getAttribute('data-collection-color-buttons-enabled') === 'false'
  );

  const getDefaultColorSettingMarker = () => {
    const productSectionMarker = document.querySelector(
      `${DEFAULT_COLOR_MARKER_SELECTOR}[data-collection-default-color-owner="product-section"]`
    );
    if (productSectionMarker) return productSectionMarker;

    const markers = Array.from(document.querySelectorAll(DEFAULT_COLOR_MARKER_SELECTOR));
    return markers.length ? markers[markers.length - 1] : null;
  };

  const getConfiguredDefaultColor = () => {
    const marker = getDefaultColorSettingMarker();

    if (
      !marker ||
      marker.getAttribute('data-collection-default-color-enabled') !== 'true'
    ) {
      return null;
    }

    const value = marker.getAttribute('data-collection-default-color') || '';
    const key = normalizeColor(value);
    if (!key) return null;

    return {
      key,
      label: standardColorByKey.get(key)?.label || value.trim()
    };
  };

  const getCards = () => Array.from(document.querySelectorAll(CARD_SELECTOR));

  const getColorControls = (card) => Array.from(card.querySelectorAll(CONTROL_SELECTOR))
    .filter((control) => !control.disabled && normalizeColor(control.dataset.optionValue));

  const transparentColors = new Set([
    '',
    'transparent',
    'rgba(0, 0, 0, 0)',
    'rgba(0,0,0,0)'
  ]);

  const fallbackColorByKey = new Map(Object.entries({
    clear: '#ffffff',
    cream: '#f5e7c6',
    ivory: '#fffff0',
    natural: '#d8bd92',
    kraft: '#a87844',
    beige: '#d8c7a3',
    tan: '#c9a36b',
    brown: '#6f452d',
    burgundy: '#7f1734',
    maroon: '#800000',
    navy: '#172a54',
    'navy blue': '#172a54',
    blue: '#2366b1',
    'royal blue': '#2454a6',
    teal: '#138783',
    turquoise: '#2ab7b1',
    green: '#287a49',
    'dark green': '#225c39',
    'forest green': '#225c39',
    lime: '#8bcf2f',
    'lime green': '#75bd2b',
    yellow: '#f2cf2f',
    gold: '#c9a227',
    goldtone: '#d6b54b',
    silvertone: '#c6ccd3',
    orange: '#e77721',
    red: '#c82b32',
    pink: '#e892b4',
    purple: '#7046a5',
    lavender: '#b6a1d8',
    gray: '#898f96',
    silver: '#b9bec4',
    aluminum: '#b9bec4',
    charcoal: '#464b50'
  }));

  const getControlLabel = (control) => {
    if (control.labels?.length) return control.labels[0];
    if (control.id) {
      const escapedId = window.CSS?.escape ? window.CSS.escape(control.id) : control.id.replace(/"/g, '\\"');
      const label = control.ownerDocument.querySelector(`label[for="${escapedId}"]`);
      if (label) return label;
    }
    return control.closest('label') || control.parentElement?.querySelector('label') || null;
  };

  const getSwatchPresentation = (control, key) => {
    const label = getControlLabel(control);
    const image = label?.querySelector('img');
    const imageUrl = image?.currentSrc || image?.src || '';

    if (imageUrl) return { imageUrl, color: '', isClear: key === 'clear' };

    let color = '';
    if (label) {
      const labelStyles = window.getComputedStyle(label);
      const beforeStyles = window.getComputedStyle(label, '::before');
      color = labelStyles.getPropertyValue('--swatch-color').trim() ||
        beforeStyles.backgroundColor.trim() ||
        labelStyles.backgroundColor.trim();
      if (transparentColors.has(color.toLowerCase())) color = '';
    }

    color ||= fallbackColorByKey.get(key) || '';

    return {
      imageUrl: '',
      color,
      isClear: key === 'clear' || key === 'transparent'
    };
  };

  const findColorControl = (card, colorKey) => {
    const matchingControls = getColorControls(card).filter(
      (control) => normalizeColor(control.dataset.optionValue) === colorKey
    );

    return matchingControls.find(
      (control) => !control.closest('[data-collection-color-fallback="true"]')
    ) || matchingControls[0] || null;
  };

  const makeControlAuthoritative = (card, selectedControl) => {
    getColorControls(card).forEach((control) => {
      if (control !== selectedControl) control.checked = false;
    });
  };

  const activateColorControl = (card, selectedControl) => {
    makeControlAuthoritative(card, selectedControl);
    if (selectedControl.checked) return;

    selectedControl.checked = true;
    selectedControl.dispatchEvent(new Event('change', { bubbles: true }));
  };

  const applyConfiguredDefaultColor = (cards) => {
    const configuredColor = getConfiguredDefaultColor();
    if (!configuredColor) {
      cards.forEach((card) => card.removeAttribute(DEFAULT_COLOR_APPLIED_ATTRIBUTE));
      return;
    }

    if (userSelectedColorKey) return;

    const cardsNeedingDefault = cards.filter(
      (card) => card.getAttribute(DEFAULT_COLOR_APPLIED_ATTRIBUTE) !== configuredColor.key
    );
    if (!cardsNeedingDefault.length) return;

    applyingColor = true;

    try {
      cardsNeedingDefault.forEach((card) => {
        const matchingControl = findColorControl(card, configuredColor.key);
        card.setAttribute(DEFAULT_COLOR_APPLIED_ATTRIBUTE, configuredColor.key);

        if (!matchingControl) return;

        card._applyingCollectionDefaultColor = true;
        try {
          activateColorControl(card, matchingControl);
        } finally {
          delete card._applyingCollectionDefaultColor;
        }
      });
    } finally {
      applyingColor = false;
    }
  };

  const collectColors = (cards) => {
    const colorsByKey = new Map();

    cards.forEach((card) => {
      getColorControls(card).forEach((control) => {
        const key = normalizeColor(control.dataset.optionValue);
        if (!key) return;

        const standardColor = standardColorByKey.get(key);
        const presentation = getSwatchPresentation(control, key);
        const existingColor = colorsByKey.get(key);

        if (existingColor) {
          if (!existingColor.imageUrl && presentation.imageUrl) {
            existingColor.imageUrl = presentation.imageUrl;
          }
          if (!existingColor.color && presentation.color) {
            existingColor.color = presentation.color;
          }
          existingColor.isClear ||= presentation.isClear;
          return;
        }

        colorsByKey.set(key, {
          key,
          label: standardColor?.label || String(control.dataset.optionValue).trim(),
          index: standardColor?.index ?? Number.POSITIVE_INFINITY,
          ...presentation
        });
      });
    });

    return Array.from(colorsByKey.values()).sort((first, second) => {
      if (first.index !== second.index) return first.index - second.index;
      return first.label.localeCompare(second.label, undefined, {
        numeric: true,
        sensitivity: 'base'
      });
    });
  };

  const ensureStyles = () => {
    if (document.getElementById(STYLE_ID)) return;

    const styles = document.createElement('style');
    styles.id = STYLE_ID;
    styles.textContent = `
      .collection-color-selector {
        position: relative;
        display: grid;
        grid-template-columns: minmax(0, 1fr);
        align-items: center;
        justify-items: center;
        gap: 13px;
        width: min(100%, 980px);
        max-width: 100%;
        min-width: 0;
        margin: 0 auto 26px;
        padding: 17px 20px 18px;
        box-sizing: border-box;
        border: 1px solid rgba(13, 39, 66, 0.14);
        border-radius: 12px;
        background:
          linear-gradient(110deg, rgba(245, 248, 251, 0.98), rgba(255, 255, 255, 0.96) 58%, rgba(248, 250, 252, 0.98));
        box-shadow: 0 8px 24px rgba(13, 39, 66, 0.07);
        color: var(--display-cases-blue, #262c82);
        isolation: isolate;
      }

      .collection-color-selector::before {
        position: absolute;
        top: 12px;
        bottom: 12px;
        left: 0;
        width: 4px;
        border-radius: 0 4px 4px 0;
        background: var(--display-cases-red, #b40c1c);
        content: '';
      }

      .collection-color-selector__intro {
        min-width: 0;
        text-align: center;
      }

      .collection-color-selector__heading {
        margin: 0;
        color: var(--display-cases-blue, #262c82);
        font-size: clamp(1.5rem, 1.3rem + 0.25vw, 1.8rem);
        font-weight: 750;
        letter-spacing: -0.015em;
        line-height: 1.15;
      }

      .collection-color-selector__helper {
        display: block;
        margin-top: 3px;
        color: #5d6d7e;
        font-size: 1.15rem;
        line-height: 1.3;
      }

      .collection-color-selector__buttons {
        display: flex;
        flex-wrap: wrap;
        justify-content: center;
        gap: 7px;
        width: 100%;
        max-width: 100%;
        min-width: 0;
        box-sizing: border-box;
      }

      .collection-color-selector__button {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 7px;
        min-height: 38px;
        margin: 0;
        padding: 7px 13px 7px 8px;
        border: 1px solid rgba(16, 42, 67, 0.2);
        border-radius: 999px;
        color: #243b53;
        background: rgba(255, 255, 255, 0.9);
        box-shadow: 0 1px 2px rgba(13, 39, 66, 0.06);
        font: inherit;
        font-size: 1.25rem;
        font-weight: 650;
        line-height: 1.2;
        white-space: nowrap;
        cursor: pointer;
        -webkit-tap-highlight-color: transparent;
        transition:
          transform 150ms ease,
          background-color 150ms ease,
          border-color 150ms ease,
          box-shadow 150ms ease,
          color 150ms ease;
      }

      .collection-color-selector__button:hover {
        transform: translateY(-1px);
        border-color: rgba(16, 42, 67, 0.45);
        background: #fff;
        box-shadow: 0 4px 10px rgba(13, 39, 66, 0.12);
      }

      .collection-color-selector__button[aria-pressed="true"] {
        transform: translateY(-1px);
        border-color: var(--display-cases-blue, #262c82);
        color: #fff;
        background: var(--display-cases-blue, #262c82);
        box-shadow:
          0 0 0 2px #fff,
          0 0 0 4px rgba(214, 32, 47, 0.72),
          0 5px 12px rgba(13, 39, 66, 0.18);
      }

      .collection-color-selector__button:focus-visible {
        outline: 3px solid var(--display-cases-red, #b40c1c);
        outline-offset: 3px;
      }

      .collection-color-selector__swatch {
        position: relative;
        flex: 0 0 22px;
        width: 22px;
        height: 22px;
        overflow: hidden;
        border: 1px solid rgba(16, 42, 67, 0.2);
        border-radius: 50%;
        background: linear-gradient(135deg, #e8edf2, #bac6d1);
        box-shadow:
          inset 0 0 0 1px rgba(255, 255, 255, 0.4),
          0 1px 2px rgba(13, 39, 66, 0.12);
      }

      .collection-color-selector__swatch::after {
        position: absolute;
        inset: 0;
        border-radius: inherit;
        background: linear-gradient(115deg, rgba(255, 255, 255, 0.32), transparent 42%, rgba(0, 0, 0, 0.08));
        content: '';
      }

      .collection-color-selector__button[data-collection-color-key="black"] .collection-color-selector__swatch {
        border-color: #050505;
        background: linear-gradient(145deg, #2f3338, #030405 72%);
      }

      .collection-color-selector__button[data-collection-color-key="white"] .collection-color-selector__swatch {
        border-color: #c8d0d8;
        background: linear-gradient(145deg, #fff, #f0f2f4);
      }

      .collection-color-selector__button[data-collection-color-key="maple"] .collection-color-selector__swatch {
        background:
          repeating-linear-gradient(100deg, transparent 0 4px, rgba(128, 78, 24, 0.13) 5px 6px),
          linear-gradient(145deg, #f3d49d, #d9ad69);
      }

      .collection-color-selector__button[data-collection-color-key="cherry"] .collection-color-selector__swatch {
        background:
          repeating-linear-gradient(103deg, transparent 0 5px, rgba(58, 16, 9, 0.18) 6px 7px),
          linear-gradient(145deg, #b66443, #6e241b);
      }

      .collection-color-selector__button[data-collection-color-key="walnut"] .collection-color-selector__swatch {
        background:
          repeating-linear-gradient(101deg, transparent 0 4px, rgba(18, 6, 2, 0.24) 5px 6px),
          linear-gradient(145deg, #76513b, #342017);
      }

      .collection-color-selector__button[data-collection-color-key="rustic gray"] .collection-color-selector__swatch {
        background:
          repeating-linear-gradient(102deg, transparent 0 4px, rgba(30, 37, 41, 0.16) 5px 6px),
          linear-gradient(145deg, #a3a7a6, #606765);
      }

      .collection-color-selector__button[data-collection-color-key="barnwood"] .collection-color-selector__swatch {
        background:
          repeating-linear-gradient(98deg, transparent 0 3px, rgba(47, 27, 17, 0.25) 4px 5px),
          linear-gradient(145deg, #9b8068, #5f4939);
      }

      .collection-color-selector__button[data-collection-color-key="aluminum"] .collection-color-selector__swatch {
        background: linear-gradient(115deg, #7e8991 0%, #e9eef1 33%, #9da8ae 55%, #f6f8f9 74%, #848f96 100%);
      }

      .collection-color-selector__button[aria-pressed="true"] .collection-color-selector__swatch {
        border-color: rgba(255, 255, 255, 0.75);
        box-shadow:
          0 0 0 1px rgba(16, 42, 67, 0.45),
          0 1px 3px rgba(0, 0, 0, 0.25);
      }

      .collection-color-selector__status {
        position: absolute !important;
        width: 1px !important;
        height: 1px !important;
        margin: -1px !important;
        padding: 0 !important;
        overflow: hidden !important;
        clip: rect(0 0 0 0) !important;
        white-space: nowrap !important;
        border: 0 !important;
      }

      @media (max-width: 749px) {
        .collection-color-selector {
          display: block;
          width: calc(100vw - 40px);
          max-width: calc(100vw - 40px);
          margin: 0 auto 20px;
          padding: 13px 12px 12px 16px;
          border-radius: 10px;
        }

        .collection-color-selector::before {
          top: 10px;
          bottom: 10px;
        }

        .collection-color-selector__heading {
          font-size: 1.55rem;
        }

        .collection-color-selector__helper {
          margin-top: 2px;
          font-size: 1.1rem;
        }

        .collection-color-selector__buttons {
          justify-content: flex-start;
          gap: 7px;
          margin-top: 10px;
          flex-wrap: nowrap;
          margin-right: 0;
          padding-right: 0;
          padding-bottom: 6px;
          overflow-x: auto;
          overscroll-behavior-inline: contain;
          scroll-snap-type: inline proximity;
          -webkit-overflow-scrolling: touch;
          scrollbar-width: thin;
          scrollbar-color: rgba(16, 42, 67, 0.28) transparent;
        }

        .collection-color-selector__buttons::-webkit-scrollbar {
          height: 4px;
        }

        .collection-color-selector__buttons::-webkit-scrollbar-thumb {
          border-radius: 999px;
          background: rgba(16, 42, 67, 0.28);
        }

        .collection-color-selector__button {
          flex: 0 0 auto;
          min-height: 40px;
          scroll-snap-align: start;
        }
      }

      @media (prefers-reduced-motion: reduce) {
        .collection-color-selector__button {
          transition: none;
        }
      }

      @media (forced-colors: active) {
        .collection-color-selector,
        .collection-color-selector__button,
        .collection-color-selector__swatch {
          forced-color-adjust: auto;
        }
      }
    `;
    document.head.appendChild(styles);
  };

  const createToolbar = () => {
    const element = document.createElement('div');
    element.id = TOOLBAR_ID;
    element.className = 'collection-color-selector';
    element.dataset.collectionColorSelectorToolbar = 'true';

    const intro = document.createElement('div');
    intro.className = 'collection-color-selector__intro';

    const heading = document.createElement('p');
    heading.id = `${TOOLBAR_ID}-heading`;
    heading.className = 'collection-color-selector__heading';
    heading.textContent = 'Choose a Color';

    const helper = document.createElement('span');
    helper.className = 'collection-color-selector__helper';
    helper.textContent = 'Update every available product';

    intro.append(heading, helper);

    const buttonGroup = document.createElement('div');
    buttonGroup.className = 'collection-color-selector__buttons';
    buttonGroup.dataset.collectionColorSelectorButtons = 'true';
    buttonGroup.setAttribute('role', 'group');
    buttonGroup.setAttribute('aria-labelledby', heading.id);

    const status = document.createElement('span');
    status.className = 'collection-color-selector__status';
    status.dataset.collectionColorSelectorStatus = 'true';
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');

    element.append(intro, buttonGroup, status);
    return element;
  };

  const setButtonPressedStates = (selectedKey) => {
    if (!toolbar) return;

    toolbar.querySelectorAll('[data-collection-color-key]').forEach((button) => {
      button.setAttribute(
        'aria-pressed',
        String(button.dataset.collectionColorKey === selectedKey)
      );
    });
  };

  const everyApplicableCardUsesColor = (cards, colorKey) => {
    let matchingCardCount = 0;

    const allMatchingCardsAreSelected = cards.every((card) => {
      const matchingControl = findColorControl(card, colorKey);

      if (!matchingControl) return true;
      matchingCardCount += 1;
      return matchingControl.checked;
    });

    return matchingCardCount > 0 && allMatchingCardsAreSelected;
  };

  const getUniformlySelectedColor = (cards) => {
    let selectedKey = null;
    let cardWithColorCount = 0;

    for (const card of cards) {
      const controls = getColorControls(card);
      if (!controls.length) continue;

      const selectedControl = controls.find((control) => control.checked);
      if (!selectedControl) return null;

      const cardColorKey = normalizeColor(selectedControl.dataset.optionValue);
      if (!cardColorKey) return null;

      if (selectedKey === null) {
        selectedKey = cardColorKey;
      } else if (selectedKey !== cardColorKey) {
        return null;
      }

      cardWithColorCount += 1;
    }

    return cardWithColorCount > 0 ? selectedKey : null;
  };

  const synchronizePressedState = () => {
    if (!toolbar) return;

    const cards = getCards();
    let selectedKey = null;

    if (
      userSelectedColorKey &&
      everyApplicableCardUsesColor(cards, userSelectedColorKey)
    ) {
      selectedKey = userSelectedColorKey;
    } else {
      userSelectedColorKey = null;
      selectedKey = getUniformlySelectedColor(cards);
    }

    const matchingButton = Array.from(
      toolbar.querySelectorAll('[data-collection-color-key]')
    ).find((button) => button.dataset.collectionColorKey === selectedKey);
    setButtonPressedStates(matchingButton ? selectedKey : null);
  };

  const announceSelection = (label, productCount) => {
    const status = toolbar?.querySelector('[data-collection-color-selector-status]');
    if (!status) return;

    status.textContent = `${label} selected for ${productCount} ${
      productCount === 1 ? 'product' : 'products'
    }.`;
  };

  const applyColorToCards = (colorKey, label, announce = true) => {
    const cards = getCards();
    let applicableProductCount = 0;

    applyingColor = true;
    setButtonPressedStates(colorKey);

    try {
      cards.forEach((card) => {
        const matchingControl = findColorControl(card, colorKey);

        if (!matchingControl) return;
        applicableProductCount += 1;

        activateColorControl(card, matchingControl);
      });
    } finally {
      applyingColor = false;
    }

    synchronizePressedState();
    if (announce) announceSelection(label, applicableProductCount);
  };

  const renderButtons = (colors) => {
    const buttonGroup = toolbar.querySelector('[data-collection-color-selector-buttons]');
    const signature = colors.map((color) => (
      `${color.key}:${color.label}:${color.imageUrl || ''}:${color.color || ''}:${color.isClear ? 'clear' : ''}`
    )).join('|');

    if (buttonGroup.dataset.colorSignature === signature) return;

    buttonGroup.dataset.colorSignature = signature;
    buttonGroup.replaceChildren();

    colors.forEach((color) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'collection-color-selector__button';
      button.dataset.collectionColorKey = color.key;
      button.setAttribute('aria-pressed', 'false');
      button.setAttribute('aria-label', `Select ${color.label} for all applicable products`);

      const swatch = document.createElement('span');
      swatch.className = 'collection-color-selector__swatch';
      swatch.setAttribute('aria-hidden', 'true');

      if (color.imageUrl) {
        swatch.style.backgroundImage = `url("${String(color.imageUrl).replace(/"/g, '%22')}")`;
        swatch.style.backgroundPosition = 'center';
        swatch.style.backgroundRepeat = 'no-repeat';
        swatch.style.backgroundSize = 'cover';
      } else if (color.isClear) {
        swatch.style.backgroundColor = '#eaf6fa';
        swatch.style.backgroundImage =
          'linear-gradient(120deg, transparent 0 22%, rgba(255, 255, 255, 0.95) 23% 39%, transparent 40% 100%),' +
          'linear-gradient(145deg, #ffffff 0%, #e5f3f7 38%, #abd1dc 52%, #f8fdff 70%, #cfe8ef 100%)';
        swatch.style.backgroundPosition = 'center';
        swatch.style.backgroundSize = 'cover';
      } else if (color.color) {
        swatch.style.background = color.color;
      }

      const label = document.createElement('span');
      label.className = 'collection-color-selector__label';
      label.textContent = color.label;

      button.append(swatch, label);
      button.addEventListener('click', () => {
        userSelectedColorKey = color.key;
        applyColorToCards(color.key, color.label);
      });
      buttonGroup.appendChild(button);
    });
  };

  const placeToolbar = (cards) => {
    const anchorCard = cards.find((card) => card.closest('#filter-results')) ||
      cards.find((card) => card.closest('.color-product-group')) ||
      cards[0];

    if (!anchorCard) return;

    const filterResults = anchorCard.closest('#filter-results');
    if (filterResults) {
      toolbar.dataset.collectionColorToolbarPlacement = 'filter-results';
      if (toolbar.parentElement !== filterResults || filterResults.firstElementChild !== toolbar) {
        filterResults.insertBefore(toolbar, filterResults.firstElementChild);
      }
      return;
    }

    const colorGroup = anchorCard.closest('.color-product-group');
    if (colorGroup) {
      toolbar.dataset.collectionColorToolbarPlacement = 'color-group';
      const colorGroupInner = colorGroup.querySelector('.color-product-group__inner') || colorGroup;
      const finishNavigation = colorGroupInner.querySelector('.color-product-group__jump-nav');

      if (finishNavigation) {
        if (
          toolbar.parentElement !== colorGroupInner ||
          finishNavigation.nextElementSibling !== toolbar
        ) {
          finishNavigation.after(toolbar);
        }
      } else if (
        toolbar.parentElement !== colorGroupInner ||
        colorGroupInner.firstElementChild !== toolbar
      ) {
        colorGroupInner.insertBefore(toolbar, colorGroupInner.firstElementChild);
      }
      return;
    }

    const list = anchorCard.closest('li')?.parentElement;
    if (list?.parentElement) {
      toolbar.dataset.collectionColorToolbarPlacement = 'product-list';
      if (toolbar.parentElement !== list.parentElement || toolbar.nextElementSibling !== list) {
        list.before(toolbar);
      }
      return;
    }

    toolbar.dataset.collectionColorToolbarPlacement = 'card';
    anchorCard.before(toolbar);
  };

  const removeToolbar = () => {
    document.querySelectorAll(TOOLBAR_SELECTOR).forEach((element) => element.remove());
    toolbar = null;
  };

  const refresh = () => {
    refreshScheduled = false;

    const cards = getCards();
    applyConfiguredDefaultColor(cards);

    if (pageIsDisabled()) {
      userSelectedColorKey = null;
      removeToolbar();
      return;
    }

    const colors = collectColors(cards);

    if (!cards.length || !colors.length) {
      removeToolbar();
      return;
    }

    ensureStyles();

    const existingToolbars = Array.from(document.querySelectorAll(TOOLBAR_SELECTOR));
    toolbar = existingToolbars.shift() || createToolbar();
    existingToolbars.forEach((element) => element.remove());

    renderButtons(colors);
    placeToolbar(cards);

    const selectedColor = colors.find((color) => color.key === userSelectedColorKey);
    if (selectedColor) {
      applyColorToCards(selectedColor.key, selectedColor.label, false);
    } else {
      synchronizePressedState();
    }
  };

  const scheduleRefresh = () => {
    if (refreshScheduled) return;
    refreshScheduled = true;
    window.requestAnimationFrame(refresh);
  };

  api.refresh = scheduleRefresh;

  const nodeCanAffectSelector = (node) => {
    if (!(node instanceof Element)) return false;
    if (node.matches(TOOLBAR_SELECTOR) || node.closest(TOOLBAR_SELECTOR)) return false;

    return node.matches(CARD_SELECTOR) ||
      node.matches(PAGE_SETTING_MARKER_SELECTOR) ||
      Boolean(node.querySelector(`${CARD_SELECTOR}, ${PAGE_SETTING_MARKER_SELECTOR}`));
  };

  const observer = new MutationObserver((records) => {
    const shouldRefresh = records.some((record) => {
      if (record.type === 'attributes') return true;

      return Array.from(record.addedNodes).some(nodeCanAffectSelector) ||
        Array.from(record.removedNodes).some(nodeCanAffectSelector);
    });

    if (shouldRefresh) scheduleRefresh();
  });

  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: [
      'data-collection-color-buttons-enabled',
      'data-collection-color-selector-enabled',
      'data-collection-default-color-enabled',
      'data-collection-default-color'
    ],
    childList: true,
    subtree: true
  });

  document.addEventListener('change', (event) => {
    if (applyingColor || !event.target.matches?.(CONTROL_SELECTOR)) return;

    const card = event.target.closest(CARD_SELECTOR);
    if (card) makeControlAuthoritative(card, event.target);
    userSelectedColorKey = null;
    window.requestAnimationFrame(synchronizePressedState);
  });

  document.addEventListener('on:facet-filters:updated', scheduleRefresh);
  document.addEventListener('shopify:section:load', scheduleRefresh);
  document.addEventListener('shopify:section:unload', scheduleRefresh);
  document.addEventListener('shopify:section:reorder', scheduleRefresh);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleRefresh, { once: true });
  } else {
    scheduleRefresh();
  }
})();

